import React from 'react';
import {
  AppLayout,
  Button,
  ColumnLayout,
  Header,
  SpaceBetween,
  Container,
  Link,
  HelpPanel,
  Box,
  RadioGroup,
  StatusIndicator,
} from '@cloudscape-design/components';
import ServiceNavigation from '../ServiceNavigation.jsx';
import Breadcrumbs from '../Breadcrumbs.jsx';
import ExtendingPanel from '../ExtendingPanel.jsx';
import SynopsisPanel from '../SynopsisPanel.jsx';
import BrowserCheck from '../BrowserCheck';
import { sendAnalytics } from '../../resources/rum-provider';
import { EDGE_FUNCTIONS_JWT_CF_ENDPOINT } from '../../resources/prod-env.jsx';
import '../../styles/form.scss';

const demoModule = "jwt-auth";
const PROTECTED_URL = EDGE_FUNCTIONS_JWT_CF_ENDPOINT + '/protected/premium-article.html';

const SCENARIO_OPTIONS = [
  { value: 'valid', label: '🔑 Valid Token — valid signature and expiration' },
  { value: 'expired', label: '⏰ Expired Token — valid signature but exp in the past' },
  { value: 'invalid', label: '🔓 Invalid Signature — tampered payload' },
  { value: 'none', label: '🚫 No Token — no Authorization header' },
];

export default class JwtAuth extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      toolsIndex: 0,
      toolsOpen: false,
      selectedScenario: 'none',
      result: null,
      isTesting: false,
    };
  }

  replaceToolsContent = (index) => {
    this.setState({ toolsIndex: index, toolsOpen: true });
  };

  handleTest = async () => {
    sendAnalytics({ demo: demoModule });
    this.setState({ isTesting: true, result: null });

    const scenario = this.state.selectedScenario;
    const headers = {};

    // Generate real JWT tokens signed with the demo secret
    if (scenario !== 'none') {
      const token = await generateToken(scenario);
      headers['Authorization'] = 'Bearer ' + token;
    }

    try {
      const resp = await fetch(PROTECTED_URL, { method: 'GET', headers, cache: 'no-store' });
      const status = resp.status;
      const body = await resp.text();

      let errorMsg = '';
      if (status === 401) {
        try { errorMsg = JSON.parse(body).error || ''; } catch(e) { errorMsg = body; }
      }

      this.setState({
        isTesting: false,
        result: {
          status,
          scenario,
          errorMsg,
          isSuccess: status === 200,
          token: headers['Authorization'] ? headers['Authorization'].substring(7) : null,
          headers: {
            xCache: resp.headers.get('x-cache') || '—',
            xJwtStatus: resp.headers.get('x-jwt-status') || '—',
          }
        }
      });
    } catch (err) {
      this.setState({
        isTesting: false,
        result: { status: 0, scenario, errorMsg: 'Network error: ' + err.message, isSuccess: false, headers: {} }
      });
    }
  };

  render() {
    return (
      <AppLayout
        navigation={<ServiceNavigation />}
        breadcrumbs={<Breadcrumbs items={BreadcrumbsItems} />}
        content={
          <Content
            replaceToolsContent={this.replaceToolsContent}
            handleTest={this.handleTest}
            state={this.state}
            setState={(s) => this.setState(s)}
          />
        }
        tools={Tools[this.state.toolsIndex]}
        toolsOpen={this.state.toolsOpen}
        onToolsChange={({ detail }) => this.setState({ toolsOpen: detail.open })}
        contentType="default"
      />
    );
  }
}

// --- JWT Token Generation (uses Web Crypto API) ---
const DEMO_SECRET = 'edge-demo-jwt-secret-2024';

function base64url(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64urlEncode(str) {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateToken(scenario) {
  const header = { alg: 'HS256', typ: 'JWT' };
  const now = Math.floor(Date.now() / 1000);

  let payload;
  switch (scenario) {
    case 'valid':
      payload = { sub: 'user-demo', plan: 'premium', iat: now, exp: now + 3600 };
      break;
    case 'expired':
      payload = { sub: 'user-demo', plan: 'premium', iat: now - 7200, exp: now - 3600 };
      break;
    case 'invalid':
      payload = { sub: 'user-demo', plan: 'premium', iat: now, exp: now + 3600 };
      break;
    default:
      return '';
  }

  const headerB64 = base64urlEncode(JSON.stringify(header));
  const payloadB64 = base64urlEncode(JSON.stringify(payload));
  const signingInput = headerB64 + '.' + payloadB64;

  // Sign with HMAC-SHA256
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw', encoder.encode(DEMO_SECRET), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
  );
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(signingInput));
  let sig = base64url(signature);

  // For 'invalid' scenario, tamper the signature
  if (scenario === 'invalid') {
    sig = sig.substring(0, sig.length - 4) + 'XXXX';
  }

  return headerB64 + '.' + payloadB64 + '.' + sig;
}

function getPayloadForScenario(scenario) {
  const now = Math.floor(Date.now() / 1000);
  switch (scenario) {
    case 'valid':
      return { sub: 'user-demo', plan: 'premium', iat: now, exp: now + 3600 };
    case 'expired':
      return { sub: 'user-demo', plan: 'premium', iat: now - 7200, exp: now - 3600 };
    case 'invalid':
      return { sub: 'user-demo', plan: 'premium', iat: now, exp: now + 3600, note: '(signature will be tampered)' };
    default:
      return null;
  }
}

const Content = ({ replaceToolsContent, handleTest, state, setState }) => {
  return (
    <React.Fragment>
      <BrowserCheck />
      <SpaceBetween direction="vertical" size="l">
        <SynopsisPanel videolink="https://broadcast.amazon.com/videos/2001119">
          <SpaceBetween direction="vertical" size="s">
            <Box>
              <Box variant="strong">CloudFront Function &lt; 1ms</Box> — Protects premium content by validating
              JWT tokens at the edge with HMAC-SHA256. Invalid, expired, or missing tokens are rejected immediately
              with a 401 — the request never reaches the origin.
            </Box>
            <Box variant="strong">Architecture</Box>
            <div style={{ background: '#f8f8f8', border: '1px solid #d5dbdb', borderRadius: 8, padding: '1rem', overflowX: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', minWidth: 650 }}>
                <div style={{ textAlign: 'center', minWidth: 80 }}><div style={{ fontSize: '2rem' }}>👤</div><div style={{ fontWeight: 700, fontSize: '0.85rem' }}>Client</div><div style={{ fontSize: '0.65rem', color: '#545b64' }}>Bearer eyJ…</div></div>
                <div style={{ color: '#146eb4', fontWeight: 'bold', fontSize: '1.3rem' }}>→</div>
                <div style={{ textAlign: 'center', minWidth: 90 }}><div style={{ fontSize: '2rem' }}>☁️</div><div style={{ fontWeight: 700, fontSize: '0.85rem' }}>CloudFront</div><div style={{ fontSize: '0.7rem', color: '#545b64' }}>Viewer Request</div></div>
                <div style={{ color: '#146eb4', fontWeight: 'bold', fontSize: '1.3rem' }}>→</div>
                <div style={{ textAlign: 'center', minWidth: 130, border: '2px solid #FF9900', borderRadius: 10, padding: '0.5rem', background: 'rgba(255,153,0,0.06)' }}><div style={{ fontSize: '2rem' }}>⚡</div><div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#FF9900' }}>CF Function</div><div style={{ fontSize: '0.65rem', color: '#545b64' }}>HMAC-SHA256 verify</div></div>
                <div style={{ color: '#146eb4', fontWeight: 'bold', fontSize: '1.3rem' }}>→</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <div style={{ background: '#e8f5e9', border: '1px solid #1d8102', borderRadius: 4, padding: '0.3rem 0.6rem', fontSize: '0.8rem', color: '#1d8102', fontWeight: 600 }}>✅ 200 → S3 content</div>
                  <div style={{ background: '#fce8e6', border: '1px solid #d13212', borderRadius: 4, padding: '0.3rem 0.6rem', fontSize: '0.8rem', color: '#d13212', fontWeight: 600 }}>❌ 401 → Rejected</div>
                </div>
              </div>
            </div>
            <Box variant="strong">CloudFront Function code</Box>
            <div style={{ background: '#0d1117', borderRadius: 8, padding: '1rem', overflowX: 'auto' }}>
              <pre style={{ margin: 0, color: '#c9d1d9', fontSize: '0.82rem', lineHeight: 1.6, fontFamily: "'Courier New', monospace" }}>{`import crypto from 'crypto';

var SECRET = 'edge-demo-jwt-secret-2024';

function handler(event) {
    var request = event.request;
    var headers = request.headers;

    if (!headers.authorization) return { statusCode: 401 };

    var token = headers.authorization.value.substring(7);
    var segments = token.split('.');

    // Verify HMAC-SHA256 signature
    var signingInput = segments[0] + '.' + segments[1];
    var expectedSig = crypto.createHmac('sha256', SECRET)
        .update(signingInput).digest('base64url');

    if (expectedSig !== segments[2]) return { statusCode: 401 };

    // Check expiration
    var payload = JSON.parse(String.bytesFrom(segments[1], 'base64url'));
    if (payload.exp && Date.now() / 1000 > payload.exp)
        return { statusCode: 401 };

    return request; // Token valid → pass to origin
}`}</pre>
            </div>
            <Box variant="strong">Comparison</Box>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #d5dbdb', textAlign: 'left' }}>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Approach</th>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Latency</th>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Origin Load</th>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #eee' }}><td style={{ padding: '0.4rem 0.6rem' }}>Backend validation</td><td style={{ padding: '0.4rem 0.6rem' }}>200ms+</td><td style={{ padding: '0.4rem 0.6rem' }}>All traffic hits origin</td><td style={{ padding: '0.4rem 0.6rem' }}>High</td></tr>
                <tr style={{ borderBottom: '1px solid #eee' }}><td style={{ padding: '0.4rem 0.6rem' }}>Lambda@Edge</td><td style={{ padding: '0.4rem 0.6rem' }}>5-20ms</td><td style={{ padding: '0.4rem 0.6rem' }}>Reduced</td><td style={{ padding: '0.4rem 0.6rem' }}>Medium</td></tr>
                <tr><td style={{ padding: '0.4rem 0.6rem', fontWeight: 600 }}>CloudFront Function</td><td style={{ padding: '0.4rem 0.6rem' }}>&lt; 1ms</td><td style={{ padding: '0.4rem 0.6rem' }}>Only valid requests</td><td style={{ padding: '0.4rem 0.6rem' }}>~6x cheaper</td></tr>
              </tbody>
            </table>
          </SpaceBetween>
        </SynopsisPanel>

        <Container header={<Header variant="h2">Test Live — Real request through CloudFront</Header>}>
          <SpaceBetween direction="vertical" size="s">
            <RadioGroup
              value={state.selectedScenario}
              onChange={({ detail }) => setState({ selectedScenario: detail.value })}
              items={SCENARIO_OPTIONS}
            />
            {state.selectedScenario !== 'none' && (
              <div style={{ background: '#0d1117', borderRadius: 8, padding: '0.8rem 1rem', fontSize: '0.82rem', fontFamily: "'Courier New', monospace" }}>
                <div style={{ color: '#8b949e', marginBottom: '0.4rem' }}>JWT Payload that will be sent:</div>
                <pre style={{ margin: 0, color: '#c9d1d9', lineHeight: 1.5 }}>
                  {JSON.stringify(getPayloadForScenario(state.selectedScenario), null, 2)}
                </pre>
              </div>
            )}
            {state.selectedScenario === 'none' && (
              <div style={{ background: '#f0f0f0', borderRadius: 6, padding: '0.6rem 0.8rem', fontSize: '0.85rem', color: '#545b64' }}>
                No Authorization header will be sent — the CFF will immediately return 401.
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <Button variant="primary" onClick={handleTest} loading={state.isTesting} iconName="refresh">
                Send request to CloudFront
              </Button>
              <Box variant="small" color="text-body-secondary">
                Endpoint: <Box variant="code" display="inline">{PROTECTED_URL}</Box>
              </Box>
            </div>
            {state.result && <ResultPanel result={state.result} />}
          </SpaceBetween>
        </Container>

        <ExtendingPanel label="Extend it">
          <ColumnLayout columns={2} variant="text-grid">
            <Box>
              Questions/ideas on this demo? <Box variant="strong">Daniel Martinez, dmrubio@amazon.es</Box>
            </Box>
            <Box>
              <Link external href="https://docs.aws.amazon.com/code-library/latest/ug/cloudfront_example_cloudfront_functions_kvs_jwt_verify_section.html">
                📘 CloudFront Functions documentation
              </Link>
            </Box>
          </ColumnLayout>
        </ExtendingPanel>
      </SpaceBetween>
    </React.Fragment>
  );
};

const ResultPanel = ({ result }) => {
  const isSuccess = result.isSuccess;
  const scenarioLabels = {
    valid: '🔑 Valid Token',
    expired: '⏰ Expired Token',
    invalid: '🔓 Invalid Signature',
    none: '🚫 No Token',
  };

  return (
    <Container header={<Header variant="h2">Result</Header>}>
      <SpaceBetween direction="vertical" size="s">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{
            background: isSuccess ? '#e8f5e9' : '#fce8e6',
            border: '2px solid ' + (isSuccess ? '#1d8102' : '#d13212'),
            borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 700, fontSize: '1rem',
            color: isSuccess ? '#1d8102' : '#d13212'
          }}>
            {isSuccess ? '✅' : '❌'} HTTP {result.status}
          </div>
          <Box>{scenarioLabels[result.scenario]}</Box>
        </div>
        {result.errorMsg && (
          <Box variant="code">{result.errorMsg}</Box>
        )}
        {isSuccess && (
          <Box color="text-status-success">
            Token validated at the edge — request forwarded to S3 origin. Content served.
          </Box>
        )}
        {!isSuccess && result.status === 401 && (
          <Box color="text-status-error">
            Rejected at the edge by CloudFront Function — request never reached the origin.
          </Box>
        )}
        {result.token && (
          <div style={{ background: '#0d1117', borderRadius: 6, padding: '0.6rem 0.8rem', fontSize: '0.75rem', fontFamily: "'Courier New', monospace", overflowX: 'auto' }}>
            <div style={{ color: '#8b949e', marginBottom: '0.3rem' }}>Token sent:</div>
            <div style={{ color: '#c9d1d9', wordBreak: 'break-all' }}>{result.token}</div>
          </div>
        )}
        {result.headers && result.headers.xCache && (
          <Box variant="small" color="text-body-secondary">
            X-Cache: <Box variant="code" display="inline">{result.headers.xCache}</Box>
            {result.headers.xJwtStatus !== '—' && (<span> · X-JWT-Status: <Box variant="code" display="inline">{result.headers.xJwtStatus}</Box></span>)}
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

const BreadcrumbsItems = [
  { text: 'Home', href: '/' },
  { text: 'JWT Auth', href: '/jwtauth' },
];

const Tools = [
  <HelpPanel header={<h3>JWT Auth at Edge</h3>}>
    <p>This demo sends real HTTP requests to a CloudFront distribution. A CloudFront Function at viewer-request
    validates the JWT token using HMAC-SHA256 with a hardcoded secret.</p>
    <p>Invalid, expired, or missing tokens get an immediate 401 response — the request never reaches the origin.</p>
  </HelpPanel>,
];
